// Pure PMTiles header → Esri LOD math helper.
//
// No Esri, no MapLibre, no browser APIs. Just the Web Mercator zoom-level
// to resolution / scale computation so the basemap adapter can tell the
// operational adapter exactly which LODs to pin the MapView to, without
// loading `@arcgis/core` inside a unit test.
//
// See `FuseSimple_v1_Decisions.md` §3 (PMTiles Header → Esri Tiling Scheme
// — Timing) for why this runs before `view.when()`, and §"Zoom 0 floor"
// for why we clamp `minZoom` to 1.

/**
 * WGS84 equatorial circumference used by the Web Mercator tile grid.
 * 2 * π * 6378137 ≈ 40075016.6855784 meters.
 */
export const WEB_MERCATOR_CIRCUMFERENCE_METERS = 40075016.6855784;

/** Standard web tile edge length in pixels. */
export const TILE_SIZE_PIXELS = 256;

/**
 * Maximum zoom level to emit LODs for. Vector tiles re-render at every
 * zoom (no stretching), so we extend well beyond the PMTiles header's
 * `maxZoom` to allow full street-level zoom. MapLibre oversamples the
 * highest available tile data; labels re-place and lines stay crisp.
 */
export const MAX_OVERZOOM = 24;

/**
 * Esri's reference DPI for scale ↔ resolution conversion: 96 dots per
 * inch. Scale = resolution × (DPI / 0.0254). The `0.0254` converts
 * inches to meters: 1 inch = 0.0254 m.
 *
 * The previous constant (`559082264.0287178`) was derived independently
 * from the resolution formula, causing a 5.83% scale mismatch. Esri's
 * MapView uses *scale* (not resolution) to compute `view.resolution`
 * for rendering, so the two values must be linked by the 96-DPI
 * formula. We now derive scale from resolution at call time via
 * `scaleAtZoom()` instead of storing an independent constant.
 */
export const ESRI_DPI = 96;

/** Meters per pixel at the Esri reference DPI (96). */
export const METERS_PER_PIXEL = 0.0254 / ESRI_DPI;

/**
 * Lower bound on the zoom levels we emit. Prior art
 * (`maplibre-gl-leaflet`) reports precision / wraparound artifacts at
 * zoom 0, and real-world PMTiles archives aren't meaningful at that
 * level anyway. See `FuseSimple_v1_Decisions.md` §"Zoom 0 floor".
 */
export const MIN_ZOOM_FLOOR = 1;

/**
 * Minimal PMTiles header shape the LOD math consumes. Deliberately
 * duplicated from `src/adapters/types.ts` (`PMTilesHeader`) rather than
 * imported — this module is pure and must not depend on the adapter
 * layer. Matches the subset of fields the math actually reads.
 */
export interface PMTilesHeaderInput {
  minZoom: number;
  maxZoom: number;
}

/**
 * Esri-`LOD`-compatible shape. Named structurally rather than by
 * importing Esri so the helper stays browserless and unit-testable
 * under jsdom without loading `@arcgis/core`. An array of these can be
 * assigned directly to `view.constraints.lods`.
 */
export interface LOD {
  level: number;
  resolution: number;
  scale: number;
}

/**
 * Web Mercator meters-per-pixel at a given integer zoom.
 * Zoom 0 covers the globe in one 256-px tile, so each zoom halves the
 * resolution. Kept as a separate export because the operational adapter
 * may want to compute resolution outside the LOD-array path (e.g. for
 * log lines under the `HEADER` tag).
 */
export function resolutionAtZoom(zoom: number): number {
  return WEB_MERCATOR_CIRCUMFERENCE_METERS / (TILE_SIZE_PIXELS * 2 ** zoom);
}

/**
 * Esri-convention scale denominator at a given integer zoom. Derived
 * from resolution via the 96-DPI formula so the two are always
 * consistent: `scale = resolution / METERS_PER_PIXEL`.
 */
export function scaleAtZoom(zoom: number): number {
  return resolutionAtZoom(zoom) / METERS_PER_PIXEL;
}

/**
 * MapLibre's internal tile size in CSS pixels. MapLibre uses 512px
 * vector tiles, so its zoom-to-resolution formula is
 * `resolution = circumference / (512 × 2^zoom)`. This constant is
 * needed to convert between Esri's resolution-based zoom (which
 * interpolates linearly between LODs) and MapLibre's exponential zoom.
 */
export const MAPLIBRE_TILE_SIZE = 512;

/**
 * Convert a meters-per-CSS-pixel resolution to the equivalent MapLibre
 * fractional zoom level.
 *
 * Esri and MapLibre disagree on fractional zoom interpolation:
 * - Esri: linearly interpolates resolution between adjacent LOD entries.
 * - MapLibre: uses the exponential formula `res = circ / (512 × 2^z)`.
 *
 * At integer zoom levels both produce the same resolution, so the
 * disagreement only shows up between LODs (e.g. zoom 18.5). Passing
 * `view.zoom` straight to MapLibre would render at a different ground
 * resolution than Esri. This function solves for the MapLibre zoom that
 * produces the given resolution, keeping both canvases pixel-locked at
 * every fractional zoom.
 */
export function zoomFromResolution(resolution: number): number {
  return Math.log2(WEB_MERCATOR_CIRCUMFERENCE_METERS / (MAPLIBRE_TILE_SIZE * resolution));
}

/**
 * Apply the zoom-0 floor. Exported so callers (adapters, tests, log
 * output) can report "we clamped your minZoom" without re-implementing
 * the rule.
 */
export function clampMinZoom(minZoom: number): number {
  return Math.max(MIN_ZOOM_FLOOR, minZoom);
}

/**
 * Build the LOD array for the zoom range a PMTiles header describes.
 *
 * - Inclusive on both ends (`[minZoom, maxZoom]`).
 * - Clamps `minZoom` up to `MIN_ZOOM_FLOOR` before iterating.
 * - Throws on non-finite / negative / inverted ranges, and on the
 *   degenerate case where the entire archive sits below the zoom floor
 *   (e.g. `{ minZoom: 0, maxZoom: 0 }`) — there's nothing usable to
 *   render, so failing loudly at setup is better than a silent empty
 *   map later.
 *
 * The returned array is structurally compatible with Esri's `LOD` type.
 * Adapters are free to round-trip it through `TileInfo.create()` if
 * stricter validation is desired, but that's an implementation choice
 * of the operational adapter, not this pure helper.
 */
export function computeLODsFromHeader(header: PMTilesHeaderInput): LOD[] {
  const { minZoom, maxZoom } = header;

  if (!Number.isFinite(minZoom) || !Number.isFinite(maxZoom)) {
    throw new Error(
      `FuseSimple: PMTiles header has non-finite zoom range (minZoom=${minZoom}, maxZoom=${maxZoom})`,
    );
  }
  if (minZoom < 0 || maxZoom < 0) {
    throw new Error(
      `FuseSimple: PMTiles header zoom levels must be non-negative (minZoom=${minZoom}, maxZoom=${maxZoom})`,
    );
  }
  if (maxZoom < minZoom) {
    throw new Error(
      `FuseSimple: PMTiles header has inverted zoom range (minZoom=${minZoom}, maxZoom=${maxZoom})`,
    );
  }

  const floor = clampMinZoom(minZoom);
  if (maxZoom < floor) {
    throw new Error(
      `FuseSimple: PMTiles header zoom range [${minZoom}, ${maxZoom}] sits below the zoom-${MIN_ZOOM_FLOOR} floor — no usable LODs to emit.`,
    );
  }

  const lods: LOD[] = [];
  const ceiling = Math.max(maxZoom, MAX_OVERZOOM);
  for (let level = floor; level <= ceiling; level++) {
    lods.push({
      level,
      resolution: resolutionAtZoom(level),
      scale: scaleAtZoom(level),
    });
  }
  return lods;
}
